import { useNavigate } from "@tanstack/react-router";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CaretRight, ChartLine, Palette, Scroll, Target, TrendUp, Users, Icon } from "@phosphor-icons/react";

import { useSession } from "@/hooks/use-session";
import { mEnterpriseBrandIntelligence } from "@/mock/brand-intelligence";
import { cn } from "@/lib/utils";

const toolConfig: Record<
  string,
  {
    title: string;
    icon: Icon;
    bgColor: string;
    iconColor: string;
    description: string;
    href: string;
  }
> = {
  brand: {
    title: "Brand Foundation",
    icon: Scroll,
    bgColor: "bg-warning/10",
    iconColor: "text-warning",
    description: "Core identity, mission, vision, and global market presence across 180+ countries.",
    href: "/dashboard/research/$id/foundation",
  },
  creative_system: {
    title: "Creative System",
    icon: Palette,
    bgColor: "bg-destructive/10",
    iconColor: "text-destructive",
    description: "Visual identity, verbal guidelines, brand assets, AI memory, and compliance rules.",
    href: "/dashboard/research/$id/creative",
  },
  market_intelligence: {
    title: "Market Intelligence",
    icon: ChartLine,
    bgColor: "bg-blue-100/40",
    iconColor: "text-blue-400",
    description: "Brand health metrics, sentiment trends, market position, and product insights.",
    href: "/dashboard/research/$id/market",
  },
  competitive_intel: {
    title: "Competitive Intel",
    icon: Target,
    bgColor: "bg-purple-100/40",
    iconColor: "text-purple-400",
    description: "Competitor landscape, benchmarking, share of voice, and market disruptors.",
    href: "/dashboard/research/$id/competitive",
  },
  audience_intelligence: {
    title: "Audience Intelligence",
    icon: Users,
    bgColor: "bg-success/10",
    iconColor: "text-success",
    description: "Demographics, psychographics, behavioral insights, and market sizing.",
    href: "/dashboard/research/$id/audience",
  },
  social_and_digital: {
    title: "Social & Digital",
    icon: TrendUp,
    bgColor: "bg-sky-100/60",
    iconColor: "text-sky-500",
    description: "Platform analytics, engagement metrics, growth trends, and community intelligence.",
    href: "/dashboard/research/$id/social",
  },
};

export function BrandIntelligenceGrid() {
  const navigate = useNavigate();
  const session = useSession();
  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-48">
      {brandIntelligence &&
        Object.keys(brandIntelligence).map((item) => {
          const config = toolConfig[item];
          if (!config) return null;

          const IconComponent = config.icon;

          return (
            <Card key={item} className="group bg-card hover:shadow-lg hover:text-warning cursor-pointer" onClick={() => navigate({ to: config.href })}>
              <CardHeader>
                <div className="flex justify-between items-center gap-2">
                  <span className={cn("flex size-10 items-center justify-center rounded-lg mb-2", config.bgColor)}>
                    <IconComponent className={cn("size-6", config.iconColor)} />
                  </span>
                  <CaretRight className="size-4 text-muted-foreground group-hover:text-warning group-hover:translate-x-1 transition-transform duration-200" weight="bold" />
                </div>
                <CardTitle>{config.title}</CardTitle>
                <CardDescription>{config.description}</CardDescription>
              </CardHeader>
            </Card>
          );
        })}
    </div>
  );
}
